import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api, {
  cachedGet, getAccessToken, setAccessToken,
  setRefreshToken, clearAuthStorage,
  refreshAccessToken,
} from '@/services/api';
import { useWebSocket } from '@/hooks/useWebSocket';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getAccessToken());
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationData, setImpersonationData] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      try {
        const t = getAccessToken();
        if (!t) {
          setUser(null);
          setToken(null);
          return;
        }

        api.defaults.headers.common = api.defaults.headers.common || {};
        api.defaults.headers.common.Authorization = `Bearer ${t}`;
        setToken(t);

        const loadMe = async () => {
          const response = await api.get('/auth/me', { withCredentials: true });
          const data = response.data;
          if (data && typeof data === 'object') {
            setUser(data);
            const t2 = getAccessToken();
            if (t2) setToken(t2);
          } else {
            setUser(null);
            setToken(getAccessToken() || null);
          }
        };

        try {
          await loadMe();
        } catch (error) {
          if (error.response?.status === 401) {
            const newAccess = await refreshAccessToken();
            if (newAccess) {
              api.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
              setToken(newAccess);
              try {
                await loadMe();
              } catch (e2) {
                if (e2.response?.status === 401) {
                  clearAuthStorage();
                  setToken(null);
                  setUser(null);
                } else {
                  console.error('Auth /auth/me failed after refresh:', e2);
                }
              }
            } else {
              clearAuthStorage();
              setToken(null);
              setUser(null);
            }
          } else if (error.response) {
            console.error('Auth /auth/me HTTP error:', error.response?.status, error);
            setUser(null);
          } else {
            console.error('Auth check failed (network):', error);
            await new Promise((r) => setTimeout(r, 400));
            try {
              await loadMe();
            } catch (e2) {
              console.error('Auth retry failed:', e2);
              if (e2.response?.status === 401) {
                clearAuthStorage();
                setToken(null);
                setUser(null);
              }
            }
          }
        }

        const impStr = localStorage.getItem('impersonation');
        if (impStr) {
          try {
            const impData = JSON.parse(impStr);
            setIsImpersonating(true);
            setImpersonationData(impData);
          } catch { /* ignore corrupt data */ }
        }
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password }, { withCredentials: true });
    const { access_token: accessToken, token: legacyToken, user: userData } = response.data;
    const newToken = accessToken || legacyToken;
    if (!newToken) {
      throw new Error('Login response missing token field');
    }
    setAccessToken(newToken);
    api.defaults.headers.common = api.defaults.headers.common || {};
    api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
    if (response.data?.refresh_token) {
      setRefreshToken(response.data.refresh_token);
    }
    setToken(newToken);
    setUser(userData);

    // Demo-mode preload: pre-warm common pages after login so navigation feels instant.
    // (Dealer roles only; super_admin uses the admin portal and different data.)
    try {
      if (userData?.role !== 'super_admin') {
        setTimeout(() => {
          const did = userData?.dealership_id;
          Promise.all([
            cachedGet('/dashboard/full', 30000),
            cachedGet('/conversations', { ttl: 30000, params: { page: 1, page_size: 20 } }),
            cachedGet('/contacts', { ttl: 30000, params: { page: 1, page_size: 20 } }),
            cachedGet('/pipeline/grouped', 30000),
            did ? cachedGet(`/leaderboard/${did}?period=month&type=basic`, 60000) : Promise.resolve(null),
            cachedGet('/inventory/paged', { ttl: 60000, params: { page: 1, page_size: 24 } }),
          ]).catch(() => {});
        }, 100);
      }
    } catch {
      // ignore preload errors
    }
    return userData;
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
    const me = await api.get('/auth/me', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
    setUser(me.data);
    return me.data;
  }, [token]);

  const register = useCallback(async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name }, { withCredentials: true });
    const { access_token: accessToken, token: legacyToken, refresh_token: refreshToken, user: userData } = response.data;
    const newToken = accessToken || legacyToken;
    setAccessToken(newToken);
    api.defaults.headers.common = api.defaults.headers.common || {};
    api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }
    setToken(newToken);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      const t = getAccessToken();
      if (t) {
        await api.post('/auth/logout', {}, { headers: { Authorization: `Bearer ${t}` }, withCredentials: true });
      } else {
        await api.post('/auth/logout', {}, { withCredentials: true });
      }
    } catch {
      /* ignore */
    }
    clearAuthStorage();
    localStorage.removeItem('impersonation');
    localStorage.removeItem('admin_token');
    setToken(null);
    setUser(null);
    setIsImpersonating(false);
    setImpersonationData(null);
  }, []);

  const startImpersonation = useCallback((impersonationToken, metadata) => {
    localStorage.setItem('admin_token', getAccessToken());
    setAccessToken(impersonationToken);
    localStorage.setItem('impersonation', JSON.stringify(metadata));
    window.location.href = metadata.dealer_url || '/dashboard';
  }, []);

  const exitImpersonation = useCallback(() => {
    const adminToken = localStorage.getItem('admin_token');
    localStorage.removeItem('impersonation');
    localStorage.removeItem('admin_token');
    if (adminToken) {
      setAccessToken(adminToken);
    } else {
      clearAuthStorage();
    }
    window.location.href = '/admin/demos';
  }, []);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  const isSuperAdmin = useCallback(() => user?.role === 'super_admin', [user?.role]);

  const { subscribe: wsSubscribe, connected: wsConnected } = useWebSocket(token);
  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    changePassword,
    getAuthHeaders,
    isSuperAdmin,
    wsSubscribe,
    wsConnected,
    isImpersonating,
    impersonationData,
    startImpersonation,
    exitImpersonation,
  }), [
    user,
    token,
    loading,
    login,
    register,
    logout,
    changePassword,
    wsSubscribe,
    wsConnected,
    isImpersonating,
    impersonationData,
    getAuthHeaders,
    isSuperAdmin,
    startImpersonation,
    exitImpersonation,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
